<template>
    <div id="checkout-kiosk-hub" class="choices container">
        <!-- 7. Session Management: every kiosk screen needs a path back home -->
        <div class="mb-2">
            <button type="button" class="btn btn-kiosk-back" @click="kioskGoHome">
                <span class="fa fa-arrow-left" :title="$t('visit-information-form.back')"></span>
            </button>
        </div>

        <!-- Same title block the kiosk check-in hub shows, so both hubs open
             the same way. The heading is the account's own form title (Form
             Control > Check-out message), the same text the form behind this
             hub carries, so the guest sees one name for the form from the
             first screen on. The translated title stands in until that text
             loads, and for accounts that never saved one. -->
        <div class="hotel-title">
            <h2 v-html="checkoutTitle"></h2>
        </div>

        <!-- The account's own message for this form, straight from Form
             Control. Rich text, so it renders as saved; accounts that left it
             empty keep the generic check-out instruction. -->
        <div v-if="customHeader" class="hub-custom-header" v-html="customHeader"></div>

        <!-- Locked out after too many failed searches (same rule as the check-in hub) -->
        <div v-if="isLockedOut" class="alert alert-danger">
            {{ $t('checkin_by_name.error_locked_out') }}
        </div>

        <template v-else>
            <!-- QR is a plain button with no panel under it, so anything it has to
                 say to the guest lands here instead. -->
            <div v-if="error_msg && !active_panel" class="alert alert-danger">
                {{ error_msg }}
            </div>

            <ul class="nav nav-pills mb-3 row" role="tablist">
                <!-- QR keeps its own camera flow, so it stays a plain button -->
                <li v-if="checkoutMethods.qr" class="nav-item p-1 col-12" role="button">
                    <a class="nav-link text-center" @click="openQrPrepare">
                        <i class="fas fa-qrcode mr-2" />
                        <div>{{ $t('virtual_kiosk.by_qr_label') }}</div>
                    </a>
                </li>

                <!-- By name: the name on the reservation, against today's
                     check-outs — the same shape as the panels below it. -->
                <template v-if="checkoutMethods.name">
                    <li class="nav-item p-1 col-12" role="button">
                        <a
                            class="nav-link text-center"
                            :class="{ active: active_panel === 'name' }"
                            @click="openPanel('name')"
                            v-b-toggle.checkout-panel-name
                        >
                            <i class="fas fa-user mr-2" />
                            <div>{{ $t('virtual_kiosk.by_name_label') }}</div>
                        </a>
                    </li>
                    <b-collapse id="checkout-panel-name" class="w-100" accordion="checkout-hub-accordion" role="tabpanel">
                        <form class="kiosk-panel" @submit.prevent="submitByName">
                            <p class="panel-instruction">{{ $t('checkin_by_name.instruction') }}</p>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>
                                            {{ $t('checkin_by_name.label_guest_name') }}
                                            <small class="text-danger">*</small>
                                        </label>
                                        <input
                                            class="form-control"
                                            type="text"
                                            v-model="guest_name"
                                            :placeholder="$t('checkin_by_name.name_placeholder')"
                                            :disabled="isCheckoutNotAvailable || searching"
                                        />
                                        <small v-if="field_errors.name" class="text-danger">{{ field_errors.name }}</small>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkout_by_phone.label_checkout_date') }}</label>
                                        <input class="form-control kiosk-date" type="date" :value="checkoutDateIso" readonly tabindex="-1" />
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <button
                                    type="submit"
                                    class="btn btn-kiosk-confirm"
                                    :disabled="isCheckoutNotAvailable || searching"
                                >
                                    {{ $t('visit-information-form.confirm') }}
                                </button>
                            </div>
                            <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                        </form>
                    </b-collapse>
                </template>

                <!-- By phone -->
                <template v-if="checkoutMethods.phone">
                    <li class="nav-item p-1 col-12" role="button">
                        <a
                            class="nav-link text-center"
                            :class="{ active: active_panel === 'phone' }"
                            @click="openPanel('phone')"
                            v-b-toggle.checkout-panel-phone
                        >
                            <i class="fas fa-phone-alt mr-2" />
                            <div>{{ $t('virtual_kiosk.by_phone_label') }}</div>
                        </a>
                    </li>
                    <b-collapse id="checkout-panel-phone" class="w-100" accordion="checkout-hub-accordion" role="tabpanel">
                        <form class="kiosk-panel" @submit.prevent="submitByPhone">
                            <p class="panel-instruction">{{ $t('checkout_by_phone.instruction') }}</p>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>
                                            {{ $t('checkout_by_phone.label_phone_number') }}
                                            <small class="text-danger">*</small>
                                        </label>
                                        <vue-tel-input
                                            mode="international"
                                            :validCharactersOnly="true"
                                            v-model="phone_raw"
                                            @country-changed="phoneCountryChange"
                                            @input="phoneInput"
                                            :placeholder="$t('checkout_by_phone.phone_placeholder')"
                                            :disabled="isCheckoutNotAvailable || searching"
                                        />
                                        <small v-if="field_errors.phone" class="text-danger">{{ field_errors.phone }}</small>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkout_by_phone.label_checkout_date') }}</label>
                                        <input class="form-control kiosk-date" type="date" :value="checkoutDateIso" readonly tabindex="-1" />
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <button
                                    type="submit"
                                    class="btn btn-kiosk-confirm"
                                    :disabled="isCheckoutNotAvailable || searching"
                                >
                                    {{ $t('visit-information-form.confirm') }}
                                </button>
                            </div>
                            <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                        </form>
                    </b-collapse>
                </template>

                <!-- By email: the address on the reservation, against today's
                     check-outs — the same shape as the phone panel above. -->
                <template v-if="checkoutMethods.email">
                    <li class="nav-item p-1 col-12" role="button">
                        <a
                            class="nav-link text-center"
                            :class="{ active: active_panel === 'email' }"
                            @click="openPanel('email')"
                            v-b-toggle.checkout-panel-email
                        >
                            <i class="fas fa-envelope mr-2" />
                            <div>{{ $t('virtual_kiosk.by_email_label') }}</div>
                        </a>
                    </li>
                    <b-collapse id="checkout-panel-email" class="w-100" accordion="checkout-hub-accordion" role="tabpanel">
                        <form class="kiosk-panel" @submit.prevent="submitByEmail">
                            <p class="panel-instruction">{{ $t('virtual_kiosk.instruction_email') }}</p>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>
                                            {{ $t('visitor-checkout-form.email') }}
                                            <small class="text-danger">*</small>
                                        </label>
                                        <input
                                            class="form-control"
                                            type="email"
                                            v-model="email"
                                            placeholder="name@example.com"
                                            :disabled="isCheckoutNotAvailable || searching"
                                        />
                                        <small v-if="field_errors.email" class="text-danger">{{ field_errors.email }}</small>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkout_by_phone.label_checkout_date') }}</label>
                                        <input class="form-control kiosk-date" type="date" :value="checkoutDateIso" readonly tabindex="-1" />
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <button
                                    type="submit"
                                    class="btn btn-kiosk-confirm"
                                    :disabled="isCheckoutNotAvailable || searching"
                                >
                                    {{ $t('visit-information-form.confirm') }}
                                </button>
                            </div>
                            <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                        </form>
                    </b-collapse>
                </template>
            </ul>
        </template>

        <!-- "Prepare QR code" step -->
        <b-modal
            id="checkout-qr-prepare-modal"
            hide-header
            hide-footer
            centered
            @show="qr_modals_open++"
            @hidden="closeQrModal"
        >
            <div class="text-center py-3">
                <i class="fas fa-camera qr-prepare-icon" aria-hidden="true"></i>
                <p class="qr-prepare-text mt-3 mb-4">{{ $t('virtual_kiosk.prepare_qr') }}</p>
                <button type="button" class="btn btn-kiosk-primary px-5" @click="openQrScanner">
                    {{ $t('virtual_kiosk.ok') }}
                </button>
            </div>
        </b-modal>

        <!-- QR scanner (front camera) -->
        <b-modal
            id="checkout-qr-scan-modal"
            :title="$t('virtual_kiosk.scan_qr_title')"
            hide-footer
            centered
            @show="qr_modals_open++"
            @hidden="stopCamera(); closeQrModal()"
        >
            <div class="scanner-frame">
                <video ref="qrVideo" autoplay playsinline muted></video>
                <span class="corner tl"></span>
                <span class="corner tr"></span>
                <span class="corner bl"></span>
                <span class="corner br"></span>
            </div>

            <div v-if="camera_error" class="alert alert-danger mt-3 mb-0">
                {{ camera_error }}
            </div>

            <p v-if="scan_notice" class="scanner-notice mt-3 mb-0">{{ scan_notice }}</p>
            <p class="scanner-instruction mt-3">{{ $t('virtual_kiosk.scan_qr_instruction') }}</p>

            <!-- Auto-scan handles the normal case; Shot stays as the manual
                 retry for guests whose code needs a steadier frame. -->
            <div class="text-center">
                <button type="button" class="btn btn-kiosk-primary px-4" :disabled="!camera_ready" @click="shoot">
                    {{ $t('virtual_kiosk.shot') }} {{ shot_count }}/1
                </button>
            </div>
        </b-modal>

        <!-- 7. Session Management: the warning belongs to the parent page,
             which owns the idle watch; what starts here is the lock-out
             screen's auto-return, so this modal only ever shows the reset. -->
        <kiosk-idle-warning-modal :resetting="kiosk_auto_resetting" />
    </div>
</template>

<script>
import moment from 'moment-timezone';
import { booking_status } from '../../../utils/mockup';
import { defaultKioskConfig } from './kiosk_config';
import kioskValidationMixin from './kiosk_validation_mixin';
import KioskIdleWarningModal from './KioskIdleWarningModal.vue';
import { createQrDecoder } from './kiosk_qr_decoder';

// How often a frame is pulled off the video for decoding. Same cadence as the
// check-in hub scanner: instant to the guest, cheap enough for a tablet.
const SCAN_INTERVAL_MS = 250;

// Failed searches allowed before the hub locks itself. The counter is shared by
// every method on purpose: on an unattended kiosk, letting a guesser reset it
// by switching from phone to email would make the limit meaningless.
const MAX_ATTEMPTS = 5;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Formats the booking list is known to answer with. Parsed strictly, so an
// unexpected shape reads as "unknown date" instead of silently matching today.
const CHECKOUT_DATE_FORMATS = [
    'YYYY-MM-DD',
    'YYYYMMDD',
    'MM/DD/YYYY',
    'DD/MM/YYYY',
    'YYYY/MM/DD',
    'MMM DD, YYYY',
];

export default {
    name: 'CheckoutKioskHub',
    components: { KioskIdleWarningModal },
    mixins: [kioskValidationMixin],
    props: {
        facility: { type: Object, default: () => ({}) },
        isCheckoutNotAvailable: { type: Boolean, default: false },
        // The account's custom check-out message ({ title, header, footer }),
        // loaded in the guest's language by the parent page. Empty until that
        // fetch lands, and for an account that never saved one.
        custom_text_setting: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            camera_ready: false,
            camera_error: '',
            camera_stream: null,
            shot_count: 0,

            // Open QR modals (prepare + scanner). Counted rather than flagged
            // because the OK button opens the scanner while the prepare modal
            // is still fading out, and a flag would blink "nobody is here"
            // exactly in the gap a closing tick could land in.
            qr_modals_open: 0,

            // QR auto-scan
            scan_notice: '',
            scanned_value: '',
            qr_decoder: null,
            scan_timer: null,
            decoding: false,

            // Inline search panels ('name' | 'phone' | 'email')
            active_panel: '',
            checkout_date: '',
            searching: false,
            error_msg: '',
            failed_attempts: 0,

            // Per-field validation messages, cleared on every submit
            field_errors: { name: '', phone: '', email: '' },

            guest_name: '',
            phone_raw: '',
            phone_number: '',
            phone_country_code: '',
            email: '',
        };
    },
    computed: {
        // The date both panels show on their right-hand side. A native date
        // field, so it reads like the pre-check-in form (calendar icon, the
        // guest's own date format) — which means the YYYY/MM/DD kept in
        // checkout_date has to be re-shaped to the ISO value that field
        // takes. Display only: the field is read-only and the lookups keep
        // working from today's date themselves.
        checkoutDateIso() {
            const date = moment(this.checkout_date, 'YYYY/MM/DD', true);
            return date.isValid() ? date.format('YYYY-MM-DD') : '';
        },
        // The account's title for the check-out form, falling back to the
        // translated form name while it loads and for accounts that never
        // saved one.
        checkoutTitle() {
            const custom = String(this.custom_text_setting?.title ?? '').trim();
            return custom || this.$t('visitor-checkout-form.header');
        },
        // The account's message under the title, from the same custom message
        // the title comes from. Rich text saved on the admin portal, so it is
        // rendered as HTML the way the form behind this hub renders it.
        customHeader() {
            return String(this.custom_text_setting?.header ?? '').trim();
        },
        // QR, name and phone follow the Guest Hub "Reservation Search" setting
        // the check-in hub already reads. Email asks for nothing the guest has
        // to have kept — just the address they booked with — so it is the one
        // method a check-out kiosk always offers, and no setting removes it.
        checkoutMethods() {
            const methods = this.kiosk_config?.search_methods ?? defaultKioskConfig.search_methods;
            return {
                qr: methods.qr,
                name: methods.name,
                phone: methods.phone,
                email: true,
            };
        },
        isLockedOut() {
            return this.failed_attempts >= MAX_ATTEMPTS;
        },

        // Someone is part-way through starting a check-out here: a panel is
        // open, the camera is up, or a lookup is in flight. The parent holds
        // the closing transition back while this is true, so the end of the
        // window never wipes a half-typed phone number off the screen — and
        // arms the idle watch, so a guest who simply walks away still resets
        // the kiosk instead of leaving it open past closing time.
        guestIsEngaged() {
            if (this.isLockedOut) return false; // error screen — see the watcher below
            return this.searching || this.active_panel !== '' || this.qr_modals_open > 0;
        },
    },
    watch: {
        // MainHotelCheckoutForm loads the facility after mount.
        'facility.id': {
            immediate: true,
            async handler(id) {
                if (!id || this.kiosk_config) return;
                await this.kioskLoadConfig(id);

                // Entering the hub IS the start of a check-out session, so the
                // operating rules apply here too: a guest who deep-links this
                // URL while the Guest Hub is unconfigured or closed goes back to
                // the home screen, which shows "No Operation" / "Out of
                // Operating Time".
                if (!this.kioskCanStartCheckout()) return this.kioskGoHome();
            },
        },

        // The end of the check-out window is watched by the parent page, which
        // outlives this hub and so can hold the close back until a guest who
        // already crossed into the form is finished. All it needs from here is
        // whether anyone is part-way through the hub itself.
        guestIsEngaged: {
            immediate: true,
            handler(engaged) {
                this.$emit('busy', engaged);
            },
        },

        // 7. Session Management: the lock-out alert replaces the whole hub, so
        // it is an error screen, not a screen anyone is working on — no warning
        // to answer, just the return to the start screen a minute later. The
        // parent's idle watch lets go on its own, because a locked-out hub
        // reports nobody as engaged.
        isLockedOut(locked) {
            if (!locked) return;
            this.kioskStartAutoReturn();
        },
    },
    mounted() {
        this.checkout_date = moment().format('YYYY/MM/DD');
    },
    beforeDestroy() {
        this.stopCamera();
        // The guest crossed into the form (or left): nothing is in progress on
        // the hub any more, so the parent must not keep a stale "busy" from the
        // panel that was open when this instance went away.
        this.$emit('busy', false);
    },
    methods: {
        // ---- Panels ----------------------------------------------------

        // v-b-toggle does the open/close; this only tracks which one is active
        // so the pressed button stays highlighted and stale messages are
        // cleared. Tapping the open panel's button closes it, so drop the
        // highlight too.
        openPanel(panel) {
            this.active_panel = this.active_panel === panel ? '' : panel;
            this.resetMessages();
        },

        resetMessages() {
            this.error_msg = '';
            this.field_errors = { name: '', phone: '', email: '' };
        },

        errorDisplay(msg) {
            this.error_msg = msg;
            setTimeout(() => { this.error_msg = ''; }, 4000);
        },

        handleFailedAttempt(msg) {
            this.failed_attempts++;
            if (this.isLockedOut) {
                // The locked-out alert replaces the whole hub.
                this.error_msg = '';
                return;
            }
            this.errorDisplay(msg);
        },

        // ---- By name ---------------------------------------------------

        submitByName() {
            if (this.isLockedOut || this.searching || this.isCheckoutNotAvailable) return;
            this.resetMessages();

            const name = this.guest_name.trim();
            if (!name) {
                this.field_errors.name = this.$t('virtual_kiosk.field_required');
                return;
            }

            this.searchByName(name);
        },

        // The name on the reservation, against the stays ending today — the
        // same lookup the email panel runs, on the other field the booking
        // carries. Mode "name" is the shared listing the check-in hub searches
        // with, so what comes back is scoped to today here as well as in the
        // payload: a guest who stayed last month, or whose stay ends later in
        // the week, must not be checked out on this screen. A name two guests
        // share answers with both, and neither can be picked for the other.
        async searchByName(name) {
            const today = moment().format('YYYYMMDD');
            const payload = {
                facility_id: this.facility.id,
                mode: 'name',
                search: name,
                checkout_date: today,
                onDemand: true,
            };

            const response = await this.postBookingList('/facility-booking/list', payload);
            if (!response) return;

            const bookings = (response.data ?? []).filter(
                (b) => this.normalizeDate(b.parsed?.checkout_date) === today
            );

            if (bookings.length < 1) {
                this.handleFailedAttempt(this.$t('checkin_by_name.error_no_reservation'));
                return;
            }

            if (bookings.length > 1) {
                this.handleFailedAttempt(this.$t('checkin_by_name.error_multiple_reservation'));
                return;
            }

            await this.handoffToCheckout(bookings[0]);
        },

        // ---- By phone --------------------------------------------------

        submitByPhone() {
            if (this.isLockedOut || this.searching || this.isCheckoutNotAvailable) return;
            this.resetMessages();

            if (!this.phone_number) {
                this.field_errors.phone = this.$t('virtual_kiosk.field_required');
                return;
            }

            this.searchByPhone();
        },

        async searchByPhone() {
            const today = moment().format('YYYYMMDD');
            const payload = {
                facility_id: this.facility.id,
                mode: 'phone-checkout',
                search: `${this.phone_country_code}|${this.phone_number.replace(/\D/g, '')}`,
                checkout_date: today,
                onDemand: true,
            };

            const response = await this.postBookingList('/facility-booking/list', payload);
            if (!response) return;

            const bookings = response.data ?? [];

            if (bookings.length < 1) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_no_reservation'));
                return;
            }

            if (bookings.length > 1) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_multiple_reservation'));
                return;
            }

            const booking = bookings[0];

            // The search is already scoped to today, but a booking whose
            // check-out date says otherwise must not be checked out here.
            if (this.normalizeDate(booking.parsed?.checkout_date) !== today) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_no_reservation'));
                return;
            }

            this.finalizeBooking(response, {
                email: booking.facility_visitor_log?.visitor?.user?.email
                    || booking.metadata?.email_address
                    || booking.parsed?.email_address
                    || '',
                pass: booking.pass ?? '',
            });
        },

        normalizeDate(dateStr) {
            const parsed = moment(dateStr, CHECKOUT_DATE_FORMATS, true);
            return parsed.isValid() ? parsed.format('YYYYMMDD') : null;
        },

        // ---- By email ----------------------------------------------------

        submitByEmail() {
            if (this.isLockedOut || this.searching || this.isCheckoutNotAvailable) return;
            this.resetMessages();

            const email = this.email.trim();

            // The kiosk keyboard makes typos easy; catch the obvious ones before
            // spending one of the guest's attempts on a lookup that cannot match.
            if (!email) this.field_errors.email = this.$t('virtual_kiosk.field_required');
            else if (!EMAIL_PATTERN.test(email)) this.field_errors.email = this.$t('checkout_by_phone.email_invalid');

            if (this.field_errors.email) return;

            this.searchByEmail(email);
        },

        // The address alone, against the stays ending today — the same lookup
        // the phone panel runs, on the other field the reservation carries.
        async searchByEmail(email) {
            const today = moment().format('YYYYMMDD');
            const payload = {
                facility_id: this.facility.id,
                mode: 'email',
                search: email,
                checkout_date: today,
                onDemand: true,
            };

            const response = await this.postBookingList('/facility-booking/list', payload);
            if (!response) return;

            // Scoped to today here as well as in the payload: "email" is the
            // shared address lookup, so what comes back can still hold a past
            // stay or one that ends later in the week, and a mailbox that
            // booked several rooms answers with all of them.
            const bookings = (response.data ?? []).filter(
                (b) => this.normalizeDate(b.parsed?.checkout_date) === today
            );

            if (bookings.length < 1) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_no_reservation'));
                return;
            }

            if (bookings.length > 1) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_multiple_reservation'));
                return;
            }

            await this.handoffToCheckout(bookings[0], email);
        },

        // ---- Hand-off ----------------------------------------------------

        // The by-email and by-reservation-number lookups answer with the
        // check-in listing shape, which the check-out summary cannot render:
        // its data template walks `parsed.*` through code that dereferences the
        // booking's visitor log, so a record without one renders every row as
        // "--". They are used to recover the email / pass pair only, and the
        // booking itself is fetched below — the same v2 call the non-kiosk
        // check-out form makes, on the shape the rest of the form is built for.
        // (By phone stays on its own result: mode "phone-checkout" is the
        // check-out listing, and already carries the visitor log.)
        async handoffToCheckout(booking, fallbackEmail = '') {
            const email = booking.facility_visitor_log?.visitor?.user?.email
                || booking.metadata?.email_address
                || booking.parsed?.email_address
                || fallbackEmail;
            const pass = booking.pass ?? '';

            // The reservation is there, but not in a form that can be checked
            // out here — without the pass there is nothing to look it up with.
            if (!email || !pass) {
                this.handleFailedAttempt(this.$t('visitor-checkout-form.error_message'));
                return;
            }

            await this.searchByEmailPass(email, pass);
        },

        // ---- By email + pass (QR code) -----------------------------------

        // Same two-mode lookup the non-kiosk check-out form runs: the booking is
        // normally found by the pre-check-in pass, and falls back to the
        // form-log pass for guests whose reservation was matched by staff.
        async searchByEmailPass(email, pass) {
            const payload = {
                facility_id: this.facility.id,
                pass,
                email,
                mode: 'pre-checkin',
                intentMode: 'checkout',
            };

            let response = await this.postBookingList('/v2/facility-booking/list', payload);
            if (!response) return;

            if ((response.data ?? []).length === 0) {
                response = await this.postBookingList('/v2/facility-booking/list', {
                    ...payload,
                    mode: 'by-pass-email-with-formlog',
                });
                if (!response) return;
            }

            const bookings = response.data ?? [];

            if (bookings.length < 1) {
                this.handleFailedAttempt(this.$t('visitor-checkout-form.error_message'));
                return;
            }

            // The form-log lookup answers for the address on the visitor record,
            // which is not always the one typed here.
            const matchedEmail = bookings[0].facility_visitor_log?.visitor?.user?.email || email;

            this.finalizeBooking(response, { email: matchedEmail, pass });
        },

        // ---- Shared request --------------------------------------------

        // Returns the booking-list body, or null when the request itself failed.
        // A transport/server failure is not a wrong answer from the guest, so it
        // must not burn one of their attempts.
        async postBookingList(url, payload) {
            this.searching = true;

            const response = await this.$lionheart
                .post(url, payload)
                .then((r) => r.data)
                .catch((e) => this.catcher(e));

            this.searching = false;

            if (response?.error?.has_error) {
                this.errorDisplay(response.error.msg);
                return null;
            }

            return response ?? null;
        },

        // ---- Result ----------------------------------------------------

        // Only a booking that is actually checked in can be checked out. The
        // parent takes it from here (payment, confirmation, submit).
        finalizeBooking(response, credentials) {
            const booking = response.data[0];

            // No attempt burned: the guest gave details good enough to find
            // their reservation, so the answer is about its state (already
            // checked out, never checked in) and not a wrong guess.
            if (booking.booking_status_id !== booking_status.checked_in) {
                this.errorDisplay(this.$t('visit-information-form.no_reservation_msg'));
                return;
            }

            this.$emit('booking-found', response, credentials);
        },

        // ---- Phone input -----------------------------------------------

        phoneCountryChange(v) {
            this.phone_country_code = v.dialCode;
        },

        phoneInput(v, o) {
            // Get phone number only (excluding country code)
            this.phone_number = o.number.significant;
        },

        // ---- QR flow ---------------------------------------------------

        closeQrModal() {
            this.qr_modals_open = Math.max(0, this.qr_modals_open - 1);
        },

        openQrPrepare() {
            if (this.isCheckoutNotAvailable) return;
            this.active_panel = '';
            this.resetMessages();
            this.$bvModal.show('checkout-qr-prepare-modal');
        },

        async openQrScanner() {
            this.$bvModal.hide('checkout-qr-prepare-modal');
            this.camera_error = '';
            this.scan_notice = '';
            this.scanned_value = '';
            this.shot_count = 0;
            this.$bvModal.show('checkout-qr-scan-modal');

            try {
                // Front camera: the guest holds the QR code up facing the tablet.
                this.camera_stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user' },
                    audio: false,
                });
                await this.$nextTick();
                this.$refs.qrVideo.srcObject = this.camera_stream;
                this.camera_ready = true;
            } catch (e) {
                this.camera_error = this.$t('virtual_kiosk.camera_error');
                return;
            }

            this.qr_decoder = await createQrDecoder();
            // The modal may already have been dismissed while the decoder loaded.
            if (this.camera_ready) this.startScanLoop();
        },

        // Polls the live video until a QR code is readable — the guest never has
        // to press anything.
        startScanLoop() {
            this.stopScanLoop();
            this.scan_timer = setInterval(() => this.scanFrame(), SCAN_INTERVAL_MS);
        },

        stopScanLoop() {
            if (this.scan_timer) {
                clearInterval(this.scan_timer);
                this.scan_timer = null;
            }
        },

        // Reads the current frame. Returns the decoded payload, or null when
        // this frame holds no readable code.
        async decodeCurrentFrame() {
            const video = this.$refs.qrVideo;
            if (!video || !this.qr_decoder) return null;
            // HAVE_CURRENT_DATA — anything less has no pixels to read yet.
            if (video.readyState < 2 || !video.videoWidth) return null;

            try {
                return await this.qr_decoder(video);
            } catch (e) {
                return null;
            }
        },

        async scanFrame() {
            // Frames arrive faster than a decode takes; skip while one is running.
            if (this.decoding || this.scanned_value) return;
            this.decoding = true;

            try {
                const value = await this.decodeCurrentFrame();
                if (value) this.onQrDecoded(value);
            } finally {
                this.decoding = false;
            }
        },

        // Manual retry — same decode, but tells the guest when the frame was
        // unreadable instead of failing silently.
        async shoot() {
            if (!this.camera_ready || this.decoding) return;
            this.decoding = true;

            try {
                const value = await this.decodeCurrentFrame();
                if (value) {
                    this.onQrDecoded(value);
                } else {
                    this.scan_notice = this.$t('virtual_kiosk.qr_not_detected');
                }
            } finally {
                this.decoding = false;
            }
        },

        onQrDecoded(value) {
            if (this.scanned_value) return; // first read wins
            this.scanned_value = value;
            console.log('QR code scanned:', value);
            this.shot_count = 1;

            this.stopScanLoop();
            this.stopCamera();
            this.$bvModal.hide('checkout-qr-scan-modal');

            this.resolveQrCode(value);
        },

        // The pre-check-in mail encodes one of two things in the QR, decided
        // by the facility's "check-in by reservation number" setting
        // (CheckinMailer@...): "email|pass" normally, or the PMS reservation
        // number when that setting is on. Both are read here — the same two
        // forms the check-in hub handles — so a guest is never told their own
        // confirmation mail is unreadable.
        resolveQrCode(value) {
            const code = String(value ?? '').trim();
            if (!code) {
                this.errorDisplay(this.$t('virtual_kiosk.qr_not_detected'));
                return;
            }

            if (code.includes('|')) {
                const [email, pass] = code.split('|');

                // Any other QR code carrying a pipe (a menu link, a room key)
                // decodes fine but is not a pair of credentials — say so rather
                // than running a lookup that cannot match.
                if (!email?.trim() || !pass?.trim()) {
                    this.handleFailedAttempt(this.$t('virtual_kiosk.qr_checkout_invalid'));
                    return;
                }

                this.searchByEmailPass(email.trim(), pass.trim());
                return;
            }

            this.searchByReservationNo(code);
        },

        // Mode "reservation" matches pms_reservation_no, the same lookup the
        // check-in hub runs for that number. It is not scoped to a date, so the
        // stay still has to be the one ending today before it can be checked
        // out here.
        async searchByReservationNo(reservationNo) {
            const payload = {
                facility_id: this.facility.id,
                mode: 'reservation',
                search: reservationNo,
                onDemand: true,
            };

            const response = await this.postBookingList('/facility-booking/list', payload);
            if (!response) return;

            const bookings = response.data ?? [];

            if (bookings.length < 1) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_no_reservation'));
                return;
            }

            if (bookings.length > 1) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_multiple_reservation'));
                return;
            }

            const booking = bookings[0];

            if (this.normalizeDate(booking.parsed?.checkout_date) !== moment().format('YYYYMMDD')) {
                this.handleFailedAttempt(this.$t('checkout_by_phone.error_no_reservation'));
                return;
            }

            await this.handoffToCheckout(booking);
        },

        stopCamera() {
            this.stopScanLoop();
            if (this.camera_stream) {
                this.camera_stream.getTracks().forEach((t) => t.stop());
                this.camera_stream = null;
            }
            this.camera_ready = false;
        },
    },
};
</script>

<style lang="scss" scoped>
#checkout-kiosk-hub {
    font-family: 'Poppins', sans-serif;
    touch-action: manipulation;
    /* Fill the card (main_checkout_form.vue sizes it for the kiosk) instead
       of stopping at Bootstrap's container width inside it. */
    max-width: none;

    /* Sizes below are read off the check-out mockup at iPad width (820px) and
       scale with the viewport from there: a ~38px title over ~22px body text,
       then a stack of ~140px buttons carrying ~65px labels — the same scale
       the check-in hub is drawn at, so both kiosk hubs read as one screen. */
    .hotel-title h2 {
        color: #1a5dab;
        font-family: 'Poppins';
        font-weight: 600;
        font-size: clamp(22px, 4.6vw, 40px);
        margin: 16px 0 20px;
    }

    .hub-instruction {
        font-size: clamp(15px, 2.7vw, 24px);
        color: #4d4d4d;
        margin-bottom: 16px;
    }

    /* The account's own message. Sized with the instruction it replaces so the
       screen reads the same either way, and left un-restyled otherwise: it is
       rich text, and the colors and emphasis the account saved are the point
       of it. */
    .hub-custom-header {
        font-size: clamp(15px, 2.7vw, 24px);
        line-height: 1.5;
        color: #333;
        margin-bottom: 20px;
    }

    .hub-custom-header ::v-deep p:last-child {
        margin-bottom: 0;
    }

    .nav-item {
        /* 16px between buttons, over the 8px the p-1 utility gives */
        padding: 8px 4px !important;

        a.nav-link {
            min-height: clamp(96px, 17vw, 140px);
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.4em;
            border-radius: 0;
            border: 3px solid #1489ff;
            background: #fff;
            color: #1489ff;
            font-size: clamp(28px, 8vw, 68px);
            font-weight: 700;
            line-height: 1.2;
            cursor: pointer;
            user-select: none;
            -webkit-user-select: none;

            i {
                font-size: 1em;
            }

            &.active,
            &:active {
                background: #1489ff;
                color: #fff !important;
            }
        }
    }
}

/* Inline search form revealed under the pressed method button. Same look as
   the kiosk check-in hub and the pre-check-in form's accordion
   (preCheckinCustomForm.vue): the field the guest fills on the left, the
   date on the right, underlined inputs on a plain ground. */
.kiosk-panel {
    padding: 16px 8px 8px;
    margin: 0 4px 8px;

    .panel-instruction {
        font-size: clamp(14px, 2vw, 18px);
        color: #4d4d4d;
        margin-bottom: 16px;
    }

    label {
        font-size: clamp(14px, 2vw, 18px);
        color: #4d4d4d;
        margin-bottom: 4px;
    }

    .form-control {
        min-height: 48px;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
        font-size: clamp(16px, 2.2vw, 20px);
        color: #495057;
        background-color: #fff;
        border: none;
        border-bottom: 1px solid #ced4da;
        border-radius: 0;

        &:focus {
            border-color: #ced4da;
            box-shadow: none;
        }

        &::placeholder {
            color: #cccccc;
            font-size: clamp(14px, 2vw, 18px);
        }

        /* Bootstrap greys out read-only fields; the date is meant to read
           like the other input, only fixed. Disabled stays grey: that is the
           "check-out not available" state and it should look switched off. */
        &[readonly]:not([disabled]) {
            background-color: #fff;
        }
    }

    /* The native date field is display only: a fixed value the lookups own,
       so it takes no taps (Safari opens the picker on read-only date inputs)
       and no focus. */
    .kiosk-date {
        pointer-events: none;
    }

    /* vue-tel-input renders its own input, so the underline and the size
       have to be pushed past the scoped boundary to reach it. */
    .vue-tel-input {
        min-height: 48px;
        border: none;
        border-bottom: 1px solid #ced4da;
        border-radius: 0;

        &:focus-within {
            border-color: #ced4da;
            box-shadow: none;
        }

        /* The flag dropdown pads itself vertically, which made the phone
           field taller than the date beside it; the 48px row is enough. */
        ::v-deep .vti__dropdown {
            padding-top: 0;
            padding-bottom: 0;
        }

        ::v-deep .vti__input,
        ::v-deep .vti__selection {
            min-height: 48px;
            font-size: clamp(16px, 2.2vw, 20px);
        }

        ::v-deep .vti__input::placeholder {
            color: #cccccc;
            font-size: clamp(14px, 2vw, 18px);
        }
    }
}

.btn-kiosk-confirm {
    color: #fff;
    min-width: 156px;
    min-height: 48px;
    border-radius: 24px;
    font-weight: 600;
    border: 1px solid #1aa8de;
    background-color: #1aa8de;
    touch-action: manipulation;

    &:not([disabled]):hover,
    &:not([disabled]):active {
        color: #fff;
        border-color: #0b89bb;
        background-color: #0b89bb;
    }
}

.qr-prepare-icon {
    font-size: 56px;
}
.qr-prepare-text {
    font-size: 18px;
    font-weight: 600;
}

.btn-kiosk-primary {
    color: #fff;
    background-color: #1565d8;
    min-height: 48px;
    font-weight: 600;
    touch-action: manipulation;

    &:hover,
    &:active {
        color: #fff;
        background-color: #0f4fae;
    }
}

.scanner-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    background: #000;
    overflow: hidden;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .corner {
        position: absolute;
        width: 28px;
        height: 28px;
        border: 3px solid #ff2d2d;

        &.tl { top: 10px; left: 10px; border-right: none; border-bottom: none; }
        &.tr { top: 10px; right: 10px; border-left: none; border-bottom: none; }
        &.bl { bottom: 10px; left: 10px; border-right: none; border-top: none; }
        &.br { bottom: 10px; right: 10px; border-left: none; border-top: none; }
    }
}

.scanner-instruction {
    color: #9e9e9e;
    font-size: 14px;
}

.scanner-notice {
    color: #d9534f;
    font-size: 14px;
    font-weight: 600;
}

.btn-kiosk-back {
    color: #fff;
    background-color: #1aa8de;
    min-width: 48px;
    min-height: 44px;
    touch-action: manipulation;

    &:hover,
    &:active {
        color: #fff;
        background-color: #0b89bb;
    }
}
</style>
